import type {CSSProperties} from 'react';
import React, {useMemo} from 'react';
import {Box, Grid} from '@mui/material';
import {Concept} from 'types';
import type {DataFormConfiguration} from 'schema/types';
import type {WorkV4} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {HeaderHeight, UXUnit} from 'theme/settings';
import {
  useGetWorkEditValue,
  useSetWorkEditValue,
  useWorkEditValue,
} from 'services/metadata';
import {Card, FlexBox, Layout, Spacer, Text} from 'components';
import {DataForm} from 'schema/form';
import {DataFormWorkPrizes} from 'schemas/renderers';
import {useWorkEditStateContext} from 'scenes/updateMetadata/contexts/WorkEditStateContext';
import {useExternalSuggestionsContext} from '../../contexts/ExternalSuggestionsContext';
import {ChangeRequestCard} from '../ChangeRequestCard';
import {CustomAgentActions} from '../CustomAgentActions';
import {WorkCardTitle} from './components/WorkCardTitle';
import {interceptWorkSetFieldValue} from './functions';
import {useWorkLevelChangeRequests} from './hooks';

const EMPTY_RELATED_DATA = {};

type Props = {
  workId: string;
};

const headerHeight = HeaderHeight * 2 + UXUnit;

const cardSx = {
  position: 'sticky',
  top: `${headerHeight}px`,
  maxHeight: `calc(100vh - ${headerHeight + UXUnit * 3}px)`,
  transition: 'all .5s',
};

const contentStyle: CSSProperties = {
  width: '100%',
  transition: 'all .5s ease-in-out',
};

export const WorkCard: React.FC<Props> = ({workId}) => {
  const {t, tLoose} = useLocalization();
  const {suggest} = useExternalSuggestionsContext();
  const WorkFormConfiguration = useMemo(
    (): Partial<DataFormConfiguration<WorkV4>> => ({
      schemaContainers: {
        dewey: 'NoContainer',
        externalDewey: 'NoContainer',
        otherTitles: 'NoContainer',
        prizes: 'NoContainer',
      },
      parts: {
        agents: {
          label: t('form.global.field.agents.label'),
          renderCustomListActions: (part, props) => (
            <CustomAgentActions part={part} {...props} />
          ),
        },
        prizes: {
          label: t('form.global.field.prizes.label'),
          showWhenReadonlyAndEmpty: false,
        },
        intellectualLevel: {
          label: t('form.global.field.intellectualLevel.label'),
          suggest: valuePath => suggest(`${Concept.work}.${valuePath}`),
        },
        links: {
          label: t('form.global.field.links.label'),
          suggest: valuePath => suggest(`${Concept.work}.${valuePath}`),
        },
        noteForCataloging: {
          label: t('form.global.field.noteForCataloging.label'),
          hideWhenEmpty: true,
        },
      },
      dataFormPartRenderers: {
        prizes: DataFormWorkPrizes,
      },
      showWhenReadonlyAndEmpty: true,
    }),
    [suggest, t],
  );
  const getEditValue = useGetWorkEditValue();
  const setEditValue = useSetWorkEditValue();
  const {schema, readonly, valid, showErrors} = useWorkEditStateContext();
  const changeRequests = useWorkLevelChangeRequests();

  return (
    <Card level="level2" scrollable={'toggle'} sx={cardSx}>
      <Card.Header>
        <Layout
          horizontal
          flex={1}
          adjustCenter
          justifyContent={'space-between'}>
          <WorkCardTitle />
          <Layout horizontal adjustCenter adjustRight>
            {showErrors && (valid === 'warning' || valid === 'error') ? (
              <>
                <Text variant="h4" kind={valid}>
                  {tLoose(`form.global.validation.${valid}.label`)}
                </Text>
                <Spacer width={4} />
              </>
            ) : null}
          </Layout>
        </Layout>
      </Card.Header>
      <Card.Content>
        <FlexBox style={contentStyle}>
          {changeRequests?.map(wcrId => (
            <Box key={wcrId} component={Grid}>
              <ChangeRequestCard key={wcrId} changeRequestId={wcrId} />
            </Box>
          ))}
          <DataForm
            id={workId}
            schema={schema}
            configuration={WorkFormConfiguration}
            // TODO: Validate: fix this
            relatedData={EMPTY_RELATED_DATA}
            mode={readonly ? 'read-only' : 'edit'}
            showErrors={showErrors}
            setFieldValueModifier={interceptWorkSetFieldValue}
            getEditValue={getEditValue}
            useEditValue={useWorkEditValue}
            setEditValue={setEditValue}
          />
        </FlexBox>
      </Card.Content>
    </Card>
  );
};
